/**
 * Form Store — Reactive state management for Form Runtime
 *
 * Serialized into the Gate iframe runtime as a self-contained
 * Vue 3 reactive store managing: field values, visibility, required,
 * readonly, disabled, errors, info messages, and step navigation.
 */

/**
 * Serialize the form store factory into a JavaScript string
 * for embedding in the Gate iframe runtime.
 */
export const serializeFormStoreForRuntime = (): string => {
    return `
(function() {

    function createFormStore(formEl) {
        var Vue = window.Vue;
        var parseRule = window.__prmParseRule;
        var evalRule = window.__prmEvalRule;

        // ── Extract field definitions from DOM ──
        var fieldEls = formEl.querySelectorAll("[name]");
        var fieldDefs = {};
        var fieldOrder = [];

        for (var i = 0; i < fieldEls.length; i++) {
            var el = fieldEls[i];
            var name = el.getAttribute("name");
            if (!name || fieldDefs[name]) continue;

            var tag = el.tagName.toLowerCase();
            var fieldType = "text";
            var match = tag.match(/^prm-(.+)-field$/);
            if (match) fieldType = match[1];

            fieldDefs[name] = {
                name: name,
                type: fieldType,
                label: el.getAttribute("label") || name,
                required: el.getAttribute("required") === "true",
                placeholder: el.getAttribute("placeholder") || "",
                map: el.getAttribute("prm:map") || "",
                source: el.getAttribute("prm:source") || "",
                display: el.getAttribute("prm:display") || "",
                defaultValue: el.getAttribute("prm:default") || "",
                minLength: el.getAttribute("minlength") || "",
                maxLength: el.getAttribute("maxlength") || "",
                pattern: el.getAttribute("pattern") || "",
                accept: el.getAttribute("accept") || "",
                variant: el.getAttribute("prm:variant") || "",
                multiple: el.hasAttribute("multiple"),
                min: el.getAttribute("min") || "",
                max: el.getAttribute("max") || "",
                currencyCode: el.getAttribute("prm:currency-code") || "",
                currencyField: el.getAttribute("prm:currency-field") || "",
                options: el.getAttribute("options") || "",
                rules: {
                    visible: el.getAttribute("prm:rule:visible") || "",
                    required: el.getAttribute("prm:rule:required") || "",
                    readonly: el.getAttribute("prm:rule:readonly") || "",
                    hidden: el.getAttribute("prm:rule:hidden") || "",
                    disabled: el.getAttribute("prm:rule:disabled") || "",
                    label: el.getAttribute("prm:rule:label") || ""
                },
                step: null
            };
            fieldOrder.push(name);
        }

        // ── Detect steps ──
        var stepEls = formEl.querySelectorAll("prm-step");
        var steps = [];
        for (var s = 0; s < stepEls.length; s++) {
            var stepEl = stepEls[s];
            steps.push({
                index: s,
                title: stepEl.getAttribute("title") || "",
                icon: stepEl.getAttribute("icon") || ""
            });

            // Associate fields to steps
            var stepFields = stepEl.querySelectorAll("[name]");
            for (var sf = 0; sf < stepFields.length; sf++) {
                var sfName = stepFields[sf].getAttribute("name");
                if (sfName && fieldDefs[sfName]) {
                    fieldDefs[sfName].step = s;
                }
            }
        }

        var isMultiStep = steps.length > 0;

        // ── Parse all rules upfront ──
        var parsedRules = {};
        var ruleWarnings = [];

        Object.keys(fieldDefs).forEach(function(name) {
            var rules = fieldDefs[name].rules;
            parsedRules[name] = {};
            Object.keys(rules).forEach(function(ruleType) {
                if (rules[ruleType]) {
                    var parsed = parseRule(rules[ruleType]);
                    parsedRules[name][ruleType] = parsed;
                    if (parsed.t === "e") {
                        ruleWarnings.push({
                            field: name,
                            rule: "prm:rule:" + ruleType,
                            message: parsed.m,
                            raw: parsed.r
                        });
                    }
                }
            });
        });

        function boundaryValue(type, raw) {
            if (raw === null || raw === undefined || raw === "") return null;
            var value = String(raw).trim();
            if (type === "number" || type === "decimal" || type === "currency") {
                var numeric = Number(value);
                return value && Number.isFinite(numeric) ? numeric : null;
            }
            if (type === "date" || type === "datetime") {
                var match = value.match(type === "date"
                    ? /^(\\d{4})-(\\d{2})-(\\d{2})$/
                    : /^(\\d{4})-(\\d{2})-(\\d{2})T(\\d{2}):(\\d{2})(?::(\\d{2}))?$/);
                if (!match) return null;
                var year = Number(match[1]);
                var month = Number(match[2]);
                var day = Number(match[3]);
                var date = new Date(Date.UTC(year, month - 1, day));
                if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
                var hours = Number(match[4] || 0);
                var minutes = Number(match[5] || 0);
                var seconds = Number(match[6] || 0);
                if (hours > 23 || minutes > 59 || seconds > 59) return null;
                return Date.UTC(year, month - 1, day, hours, minutes, seconds);
            }
            if (type === "time") {
                var parts = value.match(/^(\\d{2}):(\\d{2})(?::(\\d{2}))?$/);
                if (!parts) return null;
                var hour = Number(parts[1]);
                var minute = Number(parts[2]);
                var second = Number(parts[3] || 0);
                return hour < 24 && minute < 60 && second < 60 ? hour * 3600 + minute * 60 + second : null;
            }
            return null;
        }

        function formatBoundary(def, raw) {
            var locale = window.Gate && window.Gate.i18n && window.Gate.i18n.locale || "en-US";
            try {
                if (def.type === "currency" && def.currencyCode) {
                    return new Intl.NumberFormat(locale, { style: "currency", currency: def.currencyCode }).format(Number(raw));
                }
                if (def.type === "date") {
                    return new Intl.DateTimeFormat(locale, { year: "numeric", month: "2-digit", day: "2-digit", timeZone: "UTC" }).format(new Date(boundaryValue(def.type, raw)));
                }
                if (def.type === "datetime") {
                    return new Intl.DateTimeFormat(locale, { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "UTC" }).format(new Date(boundaryValue(def.type, raw)));
                }
                if (def.type === "time") {
                    return new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit", timeZone: "UTC" }).format(new Date(Date.UTC(2000, 0, 1) + boundaryValue(def.type, raw) * 1000));
                }
            } catch (_error) { return raw; }
            return raw;
        }

        var validationBounds = {};
        fieldOrder.forEach(function(name) {
            var def = fieldDefs[name];
            var minimumLength = def.minLength === "" ? null : Number(def.minLength);
            var maximumLength = def.maxLength === "" ? null : Number(def.maxLength);
            var lengthValid = true;
            if (minimumLength !== null && maximumLength !== null && minimumLength > maximumLength) {
                console.warn("[prm-form] Invalid bounds for field " + name, "minlength=" + def.minLength, "maxlength=" + def.maxLength);
                lengthValid = false;
            }
            var minimum = def.min === "" ? null : boundaryValue(def.type, def.min);
            var maximum = def.max === "" ? null : boundaryValue(def.type, def.max);
            if (def.min !== "" && minimum === null) console.warn("[prm-form] Invalid bound for field " + name, "min", def.min);
            if (def.max !== "" && maximum === null) console.warn("[prm-form] Invalid bound for field " + name, "max", def.max);
            if (minimum !== null && maximum !== null && minimum > maximum) {
                console.warn("[prm-form] Invalid bounds for field " + name, "min=" + def.min, "max=" + def.max);
                minimum = null;
                maximum = null;
            }
            validationBounds[name] = { lengthValid: lengthValid, minLength: minimumLength, maxLength: maximumLength, min: minimum, max: maximum };
        });

        // ── Build initial values ──
        var initialValues = {};
        fieldOrder.forEach(function(name) {
            var def = fieldDefs[name];
            if (def.defaultValue) {
                if (def.type === "boolean") {
                    initialValues[name] = def.defaultValue === "true";
                } else if (def.type === "number" || def.type === "decimal") {
                    initialValues[name] = Number(def.defaultValue) || null;
                } else if (def.type === "currency") {
                    initialValues[name] = String(def.defaultValue);
                } else {
                    initialValues[name] = def.defaultValue;
                }
            } else {
                initialValues[name] = def.type === "boolean" ? false : null;
            }
        });

        // ── Create reactive state ──
        var state = Vue.reactive({
            values: Object.assign({}, initialValues),
            currentStep: 0,
            submitting: false,
            submitted: false,
            // A3 (design_form_update_mode_and_fluss_dispatch.md): true while the update-mode
            // prefill (record.get) is in flight, so the user can't type into a field about to be
            // overwritten by the loaded record. Folded into getFieldState's disabled below so
            // every field type gets it for free instead of each one checking a second flag.
            loading: false,
            effectiveMode: "create",

            // Per-field overrides (from programmatic API)
            overrides: {},

            // Per-field errors and info
            fieldErrors: {},
            fieldInfo: {},

            // Global form errors
            formErrors: [],
            formInfo: []
        });

        var clearedHiddenFields = {};

        // ── Rule evaluation (computed-like) ──
        function getFieldState(name) {
            var def = fieldDefs[name];
            if (!def) return { visible: true, required: false, readonly: false, disabled: false, label: name };

            var rules = parsedRules[name] || {};
            var overrides = state.overrides[name] || {};
            var values = state.values;

            var visible = overrides.visible !== undefined ? overrides.visible : (rules.visible ? evalRule(rules.visible, values) : true);
            if (rules.hidden && !overrides.visible) {
                visible = !evalRule(rules.hidden, values);
            }

            var required = overrides.required !== undefined ? overrides.required : (def.required || (rules.required ? evalRule(rules.required, values) : false));
            var readonly = overrides.readonly !== undefined ? overrides.readonly : (rules.readonly ? evalRule(rules.readonly, values) : false);
            var disabled = overrides.disabled !== undefined ? overrides.disabled : (rules.disabled ? evalRule(rules.disabled, values) : false);
            // A3: loading (prefill in flight) disables every field regardless of its own rules --
            // an override that unhides/enables a field can't fight this, on purpose.
            if (state.loading) disabled = true;

            var label = def.label;
            if (overrides.label !== undefined) {
                label = overrides.label;
            }

            return { visible: visible, required: required, readonly: readonly, disabled: disabled, label: label };
        }

        var defaultValidationMessages = {
            "en-US": {
                "forms.validation.required": "{{field}} is required",
                "forms.validation.email": "Enter a valid email address",
                "forms.validation.url": "Enter a valid URL",
                "forms.validation.minlength": "Must be at least {{min}} characters",
                "forms.validation.maxlength": "Must be at most {{max}} characters",
                "forms.validation.min": "Must be at least {{min}}",
                "forms.validation.max": "Must be at most {{max}}",
                "forms.validation.password_pattern": "Password does not match the required pattern",
                "forms.validation.password_pattern_invalid": "Invalid password validation pattern",
                "forms.validation.custom": "Invalid value"
            },
            "pt-BR": {
                "forms.validation.required": "{{field}} é obrigatório",
                "forms.validation.email": "Informe um e-mail válido",
                "forms.validation.url": "Informe uma URL válida",
                "forms.validation.minlength": "Deve ter pelo menos {{min}} caracteres",
                "forms.validation.maxlength": "Deve ter no máximo {{max}} caracteres",
                "forms.validation.min": "Deve ser no mínimo {{min}}",
                "forms.validation.max": "Deve ser no máximo {{max}}",
                "forms.validation.password_pattern": "A senha não atende ao padrão obrigatório",
                "forms.validation.password_pattern_invalid": "Padrão de validação da senha inválido",
                "forms.validation.custom": "Valor inválido"
            }
        };

        function interpolateMessage(value, params) {
            var output = value;
            Object.keys(params || {}).forEach(function(paramName) {
                output = String(output).replace(new RegExp("{{\\s*" + paramName + "\\s*}}", "g"), params[paramName]);
            });
            return output;
        }

        function defaultValidationMessage(key, params, fallback) {
            var gate = window.Gate || null;
            var locale = gate && gate.i18n && gate.i18n.locale ? gate.i18n.locale : "en-US";
            var normalizedLocale = String(locale || "").toLowerCase().indexOf("pt") === 0 ? "pt-BR" : "en-US";
            var localeMessages = defaultValidationMessages[normalizedLocale] || defaultValidationMessages["en-US"];
            var catalogMessage = gate && gate.session && gate.i18n && typeof gate.i18n.lookup === "function"
                ? gate.i18n.lookup(key)
                : undefined;
            var anonymousMessage = !gate || !gate.session ? localeMessages[key] : undefined;
            var message = catalogMessage || anonymousMessage || fallback || defaultValidationMessages["en-US"][key] || key;
            return interpolateMessage(message, params || {});
        }

        function t(key, params, fallback) {
            var gate = window.Gate || null;
            var defaultMessage = defaultValidationMessage(key, params, fallback);
            if (gate && gate.i18n && typeof gate.i18n.t === "function") {
                return gate.i18n.t(key, params || {}, defaultMessage);
            }
            return defaultMessage;
        }

        var customValidators = {};

        function runCustomValidators(name, value, def) {
            var validators = (customValidators[name] || []).concat(customValidators["*"] || []);
            var errors = [];
            validators.forEach(function(fn) {
                try {
                    var result = fn(value, { field: name, definition: def, values: formApi.getValues(), form: formApi });
                    if (typeof result === "string" && result) errors.push(result);
                    else if (Array.isArray(result)) errors = errors.concat(result.filter(Boolean));
                    else if (result === false) errors.push(t("forms.validation.custom", { field: name }, "Invalid value"));
                } catch (error) {
                    errors.push(error && error.message ? error.message : t("forms.validation.custom", { field: name }, "Invalid value"));
                }
            });
            return errors;
        }

        // ── Validation ──
        function validateField(name) {
            var def = fieldDefs[name];
            if (!def) return [];
            if (def.type === "notice") return [];

            var fieldState = getFieldState(name);
            if (!fieldState.visible) return [];

            var errors = [];
            var value = state.values[name];
            var bounds = validationBounds[name];

            if (fieldState.required) {
                var empty = value === null || value === undefined || (typeof value === "string" && value.trim() === "");
                if (empty) {
                    var label = fieldState.label || name;
                    // Try i18n resolution
                    var resolvedLabel = window.Gate && window.Gate.i18n ? window.Gate.i18n.t(label, {}, label) : label;
                    errors.push(t("forms.validation.required", { field: resolvedLabel, label: resolvedLabel }, resolvedLabel + " is required"));
                }
            }

            // Type-specific validation
            if (value !== null && value !== undefined && value !== "") {
                if (def.type === "email" && typeof value === "string") {
                    var emailValue = value.trim();
                    if (!/^[^@\\s]+@[^@\\s]+\\.[^@\\s]+$/.test(emailValue)) {
                        errors.push(t("forms.validation.email", {}, "Enter a valid email address"));
                    }
                }
                if (def.type === "url" && typeof value === "string") {
                    try { new URL(value); } catch(e) {
                        errors.push(t("forms.validation.url", {}, "Invalid URL format"));
                    }
                }
                if (typeof value === "string") {
                    var stringLength = value.length;
                    var minLength = bounds.minLength;
                    var maxLength = bounds.maxLength;
                    if (bounds.lengthValid && minLength !== null && stringLength < minLength) {
                        errors.push(t("forms.validation.minlength", { min: minLength }, "Must be at least " + minLength + " characters"));
                    }
                    if (bounds.lengthValid && maxLength !== null && stringLength > maxLength) {
                        errors.push(t("forms.validation.maxlength", { max: maxLength }, "Must be at most " + maxLength + " characters"));
                    }
                    if (def.type === "password" && def.pattern) {
                        try {
                            if (!(new RegExp(def.pattern)).test(value)) {
                                errors.push(t("forms.validation.password_pattern", {}, "Password does not match the required pattern"));
                            }
                        } catch (_error) {
                            errors.push(t("forms.validation.password_pattern_invalid", {}, "Invalid password validation pattern"));
                        }
                    }
                }
                var comparable = boundaryValue(def.type, value);
                if (comparable !== null) {
                    if (bounds.min !== null && comparable < bounds.min) {
                        var displayMin = formatBoundary(def, def.min);
                        errors.push(t("forms.validation.min", { min: displayMin }, "Must be at least " + displayMin));
                    }
                    if (bounds.max !== null && comparable > bounds.max) {
                        var displayMax = formatBoundary(def, def.max);
                        errors.push(t("forms.validation.max", { max: displayMax }, "Must be at most " + displayMax));
                    }
                }
            }

            return errors.concat(runCustomValidators(name, value, def));
        }

        function validateFieldAndStore(name) {
            var errors = validateField(name);
            if (errors.length > 0) {
                state.fieldErrors[name] = errors;
                return false;
            }
            delete state.fieldErrors[name];
            return true;
        }

        function validateStep(stepIndex) {
            var allValid = true;
            fieldOrder.forEach(function(name) {
                var def = fieldDefs[name];
                if (def.step !== null && def.step !== stepIndex) return;
                if (def.step === null && isMultiStep) return;

                var errors = validateField(name);
                if (errors.length > 0) {
                    state.fieldErrors[name] = errors;
                    allValid = false;
                } else {
                    delete state.fieldErrors[name];
                }
            });
            return allValid;
        }

        function validateAll() {
            var allValid = true;
            fieldOrder.forEach(function(name) {
                var errors = validateField(name);
                if (errors.length > 0) {
                    state.fieldErrors[name] = errors;
                    allValid = false;
                } else {
                    delete state.fieldErrors[name];
                }
            });
            return allValid;
        }

        // ── Mapping ──
        function getMapping() {
            var mapping = {};
            fieldOrder.forEach(function(name) {
                var def = fieldDefs[name];
                if (def.map && def.type !== "notice") mapping[name] = def.map;
            });
            return mapping;
        }

        function getMappedValues() {
            var mapping = getMapping();
            var mapped = {};
            Object.keys(mapping).forEach(function(fieldName) {
                if (fieldDefs[fieldName] && fieldDefs[fieldName].type === "notice") return;
                var columnName = mapping[fieldName];
                var fieldState = getFieldState(fieldName);
                if (fieldState.visible) {
                    mapped[columnName] = state.values[fieldName];
                }
            });
            return mapped;
        }

        // ── Event system ──
        var changeListeners = {};
        var stepChangeListeners = [];

        function notifyFieldChange(field, value, previousValue) {
            var listeners = changeListeners[field] || [];
            listeners.forEach(function(fn) {
                try { fn(value, previousValue); } catch(e) { console.error("[prm-form] onChange error:", e); }
            });

            // Also notify wildcard listeners
            var wildcardListeners = changeListeners["*"] || [];
            wildcardListeners.forEach(function(fn) {
                try { fn(field, value, previousValue); } catch(e) { console.error("[prm-form] onChange error:", e); }
            });
        }

        function applyHiddenValueClearing() {
            fieldOrder.forEach(function(name) {
                var fieldState = getFieldState(name);
                if (fieldState.visible) {
                    delete clearedHiddenFields[name];
                    return;
                }
                if (clearedHiddenFields[name]) return;
                var previousValue = state.values[name];
                var nextValue = fieldDefs[name] && fieldDefs[name].type === "boolean" ? false : null;
                clearedHiddenFields[name] = true;
                if (previousValue !== nextValue) {
                    state.values[name] = nextValue;
                    delete state.fieldErrors[name];
                    notifyFieldChange(name, nextValue, previousValue);
                }
            });
        }

        // ── Public API ──
        var formApi = {
            // Values
            getValue: function(name) { return state.values[name]; },
            getValues: function() { return Object.assign({}, state.values); },
            setValue: function(name, value) {
                var prev = state.values[name];
                state.values[name] = value;
                if (prev !== value) {
                    if (state.fieldErrors[name]) validateFieldAndStore(name);
                    notifyFieldChange(name, value, prev);
                }
            },
            setValues: function(map) {
                Object.keys(map).forEach(function(name) { formApi.setValue(name, map[name]); });
            },

            // Field state control
            setVisible: function(name, val) {
                if (!state.overrides[name]) state.overrides[name] = {};
                state.overrides[name].visible = val;
                applyHiddenValueClearing();
            },
            setRequired: function(name, val) {
                if (!state.overrides[name]) state.overrides[name] = {};
                state.overrides[name].required = val;
            },
            setReadonly: function(name, val) {
                if (!state.overrides[name]) state.overrides[name] = {};
                state.overrides[name].readonly = val;
            },
            setDisabled: function(name, val) {
                if (!state.overrides[name]) state.overrides[name] = {};
                state.overrides[name].disabled = val;
            },
            setLabel: function(name, val) {
                if (!state.overrides[name]) state.overrides[name] = {};
                state.overrides[name].label = val;
            },
            setOptions: function(name, opts) {
                if (!state.overrides[name]) state.overrides[name] = {};
                state.overrides[name].options = opts;
            },

            // Validation
            validate: validateAll,
            validateStep: validateStep,
            validateField: validateFieldAndStore,
            addValidator: function(name, fn) {
                if (typeof fn !== "function") return;
                var key = name || "*";
                if (!customValidators[key]) customValidators[key] = [];
                customValidators[key].push(fn);
            },
            getErrors: function() { return Object.assign({}, state.fieldErrors); },
            addFieldError: function(name, msg) {
                if (!state.fieldErrors[name]) state.fieldErrors[name] = [];
                state.fieldErrors[name].push(msg);
            },
            clearFieldError: function(name) { delete state.fieldErrors[name]; },
            addFieldInfo: function(name, msg) {
                if (!state.fieldInfo[name]) state.fieldInfo[name] = [];
                state.fieldInfo[name].push(msg);
            },
            clearFieldInfo: function(name) { delete state.fieldInfo[name]; },

            // Steps
            getCurrentStep: function() { return state.currentStep; },
            getStepCount: function() { return steps.length || 1; },
            goToStep: function(idx) {
                var prev = state.currentStep;
                state.currentStep = Math.max(0, Math.min(idx, steps.length - 1));
                stepChangeListeners.forEach(function(fn) {
                    try { fn(prev, state.currentStep); } catch(e) {}
                });
            },
            nextStep: function() {
                if (state.currentStep < steps.length - 1) {
                    if (validateStep(state.currentStep)) {
                        formApi.goToStep(state.currentStep + 1);
                        return true;
                    }
                    return false;
                }
                return false;
            },
            previousStep: function() {
                if (state.currentStep > 0) {
                    formApi.goToStep(state.currentStep - 1);
                    return true;
                }
                return false;
            },

            // Mapping
            getMapping: getMapping,
            getTargetTable: function() {
                return formEl.getAttribute("prm:target-table") || "";
            },

            // Events
            onFieldChange: function(field, fn) {
                if (!changeListeners[field]) changeListeners[field] = [];
                changeListeners[field].push(fn);
            },
            onStepChange: function(fn) {
                stepChangeListeners.push(fn);
            },

            // Submit
            submit: null, // Set by PrmForm component

            // Internal accessors
            __state: state,
            __fieldDefs: fieldDefs,
            __fieldOrder: fieldOrder,
            __steps: steps,
            __isMultiStep: isMultiStep,
            __getFieldState: getFieldState,
            __ruleWarnings: ruleWarnings,
            __parsedRules: parsedRules,
            __getMappedValues: getMappedValues
        };

        Vue.watch(function() {
            return { values: state.values, overrides: state.overrides };
        }, function() {
            applyHiddenValueClearing();
        }, { deep: true });
        applyHiddenValueClearing();

        return formApi;
    }

    window.__prmCreateFormStore = createFormStore;

})();
`;
};
